/**
 * Strategy Builder - Visual Drag & Drop Component
 * Version: 6.2.0
 * 
 * A visual strategy builder with:
 * - Drag-and-drop condition blocks
 * - 100+ TradingView Built-in/Free Indicators
 * - 122+ Keyword Functions
 * - 10+ Comparison Operators
 * - Trade leg configuration
 * - Real-time validation
 * 
 * Indicators sourced from: https://www.tradingview.com/pine-script-reference/v5/
 */

import React, { useState, useCallback, useRef, useMemo, DragEvent } from 'react'
import {
  Plus,
  Trash2,
  GripVertical,
  ChevronDown,
  ChevronRight,
  Check,
  AlertCircle,
  Play,
  Save,
  RotateCcw,
  Copy,
  Settings,
  Zap,
  TrendingUp,
  TrendingDown,
  Activity,
  BarChart2,
  BarChart,
  AlertTriangle,
  Clock,
  Target,
  Layers,
  ArrowRight,
  LineChart,
  PieChart,
  Gauge,
  Waves,
  History,
  Code,
  Search,
  Minimize2,
  Sigma,

  Crosshair,
} from 'lucide-react'
import './StrategyBuilder.css'

// Types
interface Indicator {
  id: string
  name: string
  shortName: string
  category: 'trend' | 'momentum' | 'volatility' | 'volume' | 'overlay' | 'custom'
  icon: React.ReactNode
  defaultParams: Record<string, number>
  color: string
  description: string
  parameters: string[]
}

interface Operator {
  id: string
  name: string
  symbol: string
  description: string
  requiresValue: boolean
  requiresIndicator?: boolean
}

interface Condition {
  id: string
  indicator: Indicator | null
  operator: Operator | null
  value: string
  comparisonIndicator?: Indicator
  enabled: boolean
}

interface TradeLeg {
  id: string
  action: 'buy' | 'sell' | 'close'
  orderType: 'market' | 'limit' | 'stop'
  optionType?: 'ce' | 'pe' | 'none'
  quantity: number
  stopLoss: { enabled: boolean; value: number; type: 'percentage' | 'points' }
  takeProfit: { enabled: boolean; value: number; type: 'percentage' | 'points' }
  trailingStop: { enabled: boolean; activationLevel: number; step: number }
}

interface ConditionGroup {
  id: string
  logic: 'and' | 'or'
  conditions: Condition[]
}

interface Strategy {
  id?: string
  name: string
  description: string
  conditionGroups: ConditionGroup[]
  tradeLegs: TradeLeg[]
  validationScore: number
  errors: string[]
  // TradetrON Integration
  executionTarget?: 'ZOBULL' | 'TRADETRON' | 'BOTH'
}

// =============================================================================
// ALL 100+ TRADINGVIEW BUILT-IN/FREE INDICATORS
// Source: https://www.tradingview.com/pine-script-reference/v5/
// =============================================================================

const INDICATORS: Indicator[] = [
  // ================ MOMENTUM (20) ================
  { id: 'rsi', name: 'Relative Strength Index', shortName: 'RSI', category: 'momentum', icon: <Activity size={16} />, defaultParams: { length: 14, source: 'close' }, color: '#f59e0b', description: 'Overbought/oversold indicator', parameters: ['length', 'source'] },
  { id: 'stoch', name: 'Stochastic Oscillator', shortName: 'STOCH', category: 'momentum', icon: <TrendingUp size={16} />, defaultParams: { k: 14, d: 3, smooth: 3 }, color: '#22c55e', description: 'Momentum oscillator', parameters: ['k', 'd', 'smooth'] },
  { id: 'stoch_rsi', name: 'Stochastic RSI', shortName: 'STOCH_RSI', category: 'momentum', icon: <Activity size={16} />, defaultParams: { length: 14, rsi_length: 14, k: 3, d: 3 }, color: '#10b981', description: 'Stochastic of RSI', parameters: ['length', 'rsi_length', 'k', 'd'] },
  { id: 'macd', name: 'MACD', shortName: 'MACD', category: 'momentum', icon: <BarChart2 size={16} />, defaultParams: { fast: 12, slow: 26, signal: 9 }, color: '#06b6d4', description: 'Moving Average Convergence Divergence', parameters: ['fast', 'slow', 'signal'] },
  { id: 'cci', name: 'Commodity Channel Index', shortName: 'CCI', category: 'momentum', icon: <LineChart size={16} />, defaultParams: { length: 20 }, color: '#14b8a6', description: 'Commodity Channel Index', parameters: ['length'] },
  { id: 'momentum', name: 'Momentum', shortName: 'MOM', category: 'momentum', icon: <TrendingUp size={16} />, defaultParams: { length: 10 }, color: '#f97316', description: 'Rate of price change', parameters: ['length'] },
  { id: 'roc', name: 'Rate of Change', shortName: 'ROC', category: 'momentum', icon: <TrendingUp size={16} />, defaultParams: { length: 12 }, color: '#ef4444', description: 'Percentage rate of change', parameters: ['length'] },
  { id: 'williams_r', name: "Williams %R", shortName: 'WILLR', category: 'momentum', icon: <Activity size={16} />, defaultParams: { length: 14 }, color: '#dc2626', description: "Williams Percent Range", parameters: ['length'] },
  { id: 'ultimate_osc', name: 'Ultimate Oscillator', shortName: 'UO', category: 'momentum', icon: <Activity size={16} />, defaultParams: { l1: 7, l2: 14, l3: 28 }, color: '#7c3aed', description: 'Multiple timeframe oscillator', parameters: ['l1', 'l2', 'l3'] },
  { id: 'aroon', name: 'Aroon Indicator', shortName: 'AROON', category: 'momentum', icon: <TrendingUp size={16} />, defaultParams: { length: 25 }, color: '#2563eb', description: 'Trend strength indicator', parameters: ['length'] },
  { id: 'awesome_osc', name: 'Awesome Oscillator', shortName: 'AO', category: 'momentum', icon: <BarChart2 size={16} />, defaultParams: { fast: 5, slow: 34 }, color: '#059669', description: 'Market momentum indicator', parameters: ['fast', 'slow'] },
  { id: 'kst', name: 'Know Sure Thing', shortName: 'KST', category: 'momentum', icon: <Activity size={16} />, defaultParams: { roc1: 10, roc2: 15, roc3: 20, roc4: 30, signal: 9 }, color: '#db2777', description: 'Momentum oscillator', parameters: ['roc1', 'roc2', 'roc3', 'roc4', 'signal'] },
  { id: 'mfi', name: 'Money Flow Index', shortName: 'MFI', category: 'momentum', icon: <BarChart size={16} />, defaultParams: { length: 14 }, color: '#ca8a04', description: 'Volume-weighted RSI', parameters: ['length'] },
  { id: 'chaikin_osc', name: 'Chaikin Oscillator', shortName: 'CO', category: 'momentum', icon: <Activity size={16} />, defaultParams: { fast: 3, slow: 10 }, color: '#0891b2', description: 'Accumulation/distribution oscillator', parameters: ['fast', 'slow'] },
  { id: 'ppo', name: 'Percentage Price Oscillator', shortName: 'PPO', category: 'momentum', icon: <Activity size={16} />, defaultParams: { fast: 12, slow: 26, signal: 9 }, color: '#06b6d4', description: 'Percentage MACD', parameters: ['fast', 'slow', 'signal'] },
  { id: 'ppo_hist', name: 'PPO Histogram', shortName: 'PPO_HIST', category: 'momentum', icon: <BarChart2 size={16} />, defaultParams: { fast: 12, slow: 26, signal: 9 }, color: '#14b8a6', description: 'PPO histogram', parameters: ['fast', 'slow', 'signal'] },
  { id: 'tsi', name: 'True Strength Index', shortName: 'TSI', category: 'momentum', icon: <Activity size={16} />, defaultParams: { long: 25, short: 13, signal: 13 }, color: '#8b5cf6', description: 'Double smoothed momentum', parameters: ['long', 'short', 'signal'] },
  { id: 'rvi', name: 'Relative Vigor Index', shortName: 'RVI', category: 'momentum', icon: <Activity size={16} />, defaultParams: { length: 10 }, color: '#ec4899', description: 'Price position in range', parameters: ['length'] },
  { id: 'cmo', name: 'Chande Momentum Oscillator', shortName: 'CMO', category: 'momentum', icon: <Activity size={16} />, defaultParams: { length: 9 }, color: '#f97316', description: 'Scaled momentum oscillator', parameters: ['length'] },
  { id: 'dpo', name: 'Detrended Price Oscillator', shortName: 'DPO', category: 'momentum', icon: <Activity size={16} />, defaultParams: { length: 14 }, color: '#a855f7', description: 'Price oscillation from MA', parameters: ['length'] },

  // ================ TREND (18) ================
  { id: 'sma', name: 'Simple Moving Average', shortName: 'SMA', category: 'trend', icon: <TrendingUp size={16} />, defaultParams: { length: 14 }, color: '#3b82f6', description: 'Simple price average', parameters: ['length'] },
  { id: 'ema', name: 'Exponential Moving Average', shortName: 'EMA', category: 'trend', icon: <TrendingUp size={16} />, defaultParams: { length: 14 }, color: '#8b5cf6', description: 'Weighted price average', parameters: ['length'] },
  { id: 'wma', name: 'Weighted Moving Average', shortName: 'WMA', category: 'trend', icon: <TrendingUp size={16} />, defaultParams: { length: 14 }, color: '#ec4899', description: 'Linearly weighted average', parameters: ['length'] },
  { id: 'vwma', name: 'Volume Weighted MA', shortName: 'VWMA', category: 'trend', icon: <BarChart size={16} />, defaultParams: { length: 20 }, color: '#06b6d4', description: 'Volume-weighted average', parameters: ['length'] },
  { id: 'hull_ma', name: 'Hull Moving Average', shortName: 'HMA', category: 'trend', icon: <TrendingUp size={16} />, defaultParams: { length: 14 }, color: '#f59e0b', description: 'Low-lag moving average', parameters: ['length'] },
  { id: 'supertrend', name: 'Supertrend', shortName: 'ST', category: 'trend', icon: <Zap size={16} />, defaultParams: { period: 10, multiplier: 3 }, color: '#22c55e', description: 'Trend following with stops', parameters: ['period', 'multiplier'] },
  { id: 'psar', name: 'Parabolic SAR', shortName: 'PSAR', category: 'trend', icon: <Zap size={16} />, defaultParams: { start: 0.02, inc: 0.02, max: 0.2 }, color: '#ef4444', description: 'Stop and reverse indicator', parameters: ['start', 'inc', 'max'] },
  { id: 'adx', name: 'Average Directional Index', shortName: 'ADX', category: 'trend', icon: <Activity size={16} />, defaultParams: { length: 14 }, color: '#a855f7', description: 'Trend strength measurement', parameters: ['length'] },
  { id: 'adx_pos', name: 'ADX +DI', shortName: 'ADX_POS', category: 'trend', icon: <TrendingUp size={16} />, defaultParams: { length: 14 }, color: '#22c55e', description: 'Positive directional movement', parameters: ['length'] },
  { id: 'adx_neg', name: 'ADX -DI', shortName: 'ADX_NEG', category: 'trend', icon: <TrendingDown size={16} />, defaultParams: { length: 14 }, color: '#ef4444', description: 'Negative directional movement', parameters: ['length'] },
  { id: 'ichimoku', name: 'Ichimoku Cloud', shortName: 'ICHIMOKU', category: 'trend', icon: <Waves size={16} />, defaultParams: { tenkan: 9, kijun: 26, senkou_b: 52 }, color: '#0ea5e9', description: 'Multi-component trend system', parameters: ['tenkan', 'kijun', 'senkou_b'] },
  { id: 'vwap', name: 'VWAP', shortName: 'VWAP', category: 'trend', icon: <BarChart2 size={16} />, defaultParams: { anchor: 'D' }, color: '#8b5cf6', description: 'Volume Weighted Average Price', parameters: ['anchor'] },
  { id: 'dema', name: 'Double EMA', shortName: 'DEMA', category: 'trend', icon: <TrendingUp size={16} />, defaultParams: { length: 14 }, color: '#14b8a6', description: 'Doubled exponential MA', parameters: ['length'] },
  { id: 'tema', name: 'Triple EMA', shortName: 'TEMA', category: 'trend', icon: <TrendingUp size={16} />, defaultParams: { length: 14 }, color: '#f97316', description: 'Tripled exponential MA', parameters: ['length'] },
  { id: 'trix', name: 'TRIX', shortName: 'TRIX', category: 'trend', icon: <Activity size={16} />, defaultParams: { length: 15 }, color: '#6366f1', description: 'Triple exponential average', parameters: ['length'] },
  { id: 'alma', name: 'Arnaud Legoux MA', shortName: 'ALMA', category: 'trend', icon: <TrendingUp size={16} />, defaultParams: { length: 9, offset: 0.85, sigma: 6 }, color: '#ec4899', description: 'Low-lag smoothing MA', parameters: ['length', 'offset', 'sigma'] },
  { id: 'kama', name: 'Kaufman Adaptive MA', shortName: 'KAMA', category: 'trend', icon: <TrendingUp size={16} />, defaultParams: { length: 14, fast: 2, slow: 30 }, color: '#14b8a6', description: 'Adaptive to volatility', parameters: ['length', 'fast', 'slow'] },
  { id: 'vortex_pos', name: 'Vortex Indicator +', shortName: 'VI_POS', category: 'trend', icon: <TrendingUp size={16} />, defaultParams: { length: 14 }, color: '#22c55e', description: 'Positive vortex', parameters: ['length'] },
  { id: 'vortex_neg', name: 'Vortex Indicator -', shortName: 'VI_NEG', category: 'trend', icon: <TrendingDown size={16} />, defaultParams: { length: 14 }, color: '#ef4444', description: 'Negative vortex', parameters: ['length'] },

  // ================ VOLATILITY (15) ================
  { id: 'bollinger_upper', name: 'Bollinger Upper Band', shortName: 'BB_UPPER', category: 'volatility', icon: <BarChart2 size={16} />, defaultParams: { length: 20, mult: 2 }, color: '#f43f5e', description: 'Upper Bollinger Band', parameters: ['length', 'mult'] },
  { id: 'bollinger_middle', name: 'Bollinger Middle Band', shortName: 'BB_MIDDLE', category: 'volatility', icon: <BarChart2 size={16} />, defaultParams: { length: 20, mult: 2 }, color: '#f59e0b', description: 'Middle Bollinger Band', parameters: ['length', 'mult'] },
  { id: 'bollinger_lower', name: 'Bollinger Lower Band', shortName: 'BB_LOWER', category: 'volatility', icon: <BarChart2 size={16} />, defaultParams: { length: 20, mult: 2 }, color: '#22c55e', description: 'Lower Bollinger Band', parameters: ['length', 'mult'] },
  { id: 'bollinger_width', name: 'Bollinger Bandwidth', shortName: 'BB_WIDTH', category: 'volatility', icon: <BarChart2 size={16} />, defaultParams: { length: 20, mult: 2 }, color: '#a855f7', description: 'Band width indicator', parameters: ['length', 'mult'] },
  { id: 'bollinger_squeeze', name: 'Bollinger Squeeze', shortName: 'BB_SQUEEZE', category: 'volatility', icon: <Minimize2 size={16} />, defaultParams: { length: 20, mult: 2 }, color: '#06b6d4', description: 'Squeeze indicator', parameters: ['length', 'mult'] },
  { id: 'atr', name: 'Average True Range', shortName: 'ATR', category: 'volatility', icon: <Gauge size={16} />, defaultParams: { length: 14 }, color: '#f97316', description: 'Market volatility measure', parameters: ['length'] },
  { id: 'keltner_upper', name: 'Keltner Upper', shortName: 'KELTNER_UPPER', category: 'volatility', icon: <BarChart2 size={16} />, defaultParams: { length: 20, multiplier: 2 }, color: '#ef4444', description: 'Upper Keltner Channel', parameters: ['length', 'multiplier'] },
  { id: 'keltner_lower', name: 'Keltner Lower', shortName: 'KELTNER_LOWER', category: 'volatility', icon: <BarChart2 size={16} />, defaultParams: { length: 20, multiplier: 2 }, color: '#22c55e', description: 'Lower Keltner Channel', parameters: ['length', 'multiplier'] },
  { id: 'donchian_upper', name: 'Donchian Upper', shortName: 'DON_UPPER', category: 'volatility', icon: <BarChart2 size={16} />, defaultParams: { length: 20 }, color: '#ef4444', description: 'Upper Donchian Channel', parameters: ['length'] },
  { id: 'donchian_lower', name: 'Donchian Lower', shortName: 'DON_LOWER', category: 'volatility', icon: <BarChart2 size={16} />, defaultParams: { length: 20 }, color: '#22c55e', description: 'Lower Donchian Channel', parameters: ['length'] },
  { id: 'donchian_middle', name: 'Donchian Middle', shortName: 'DON_MIDDLE', category: 'volatility', icon: <BarChart2 size={16} />, defaultParams: { length: 20 }, color: '#f59e0b', description: 'Middle Donchian Channel', parameters: ['length'] },
  { id: 'historical_vol', name: 'Historical Volatility', shortName: 'HV', category: 'volatility', icon: <History size={16} />, defaultParams: { length: 20, std: 252 }, color: '#7c3aed', description: 'Standard deviation of returns', parameters: ['length', 'std'] },
  { id: 'stddev', name: 'Standard Deviation', shortName: 'STDDEV', category: 'volatility', icon: <Sigma size={16} />, defaultParams: { length: 20 }, color: '#6366f1', description: 'Price volatility', parameters: ['length'] },
  { id: 'mass_index', name: 'Mass Index', shortName: 'MASS', category: 'volatility', icon: <Activity size={16} />, defaultParams: { fast: 9, slow: 25 }, color: '#ec4899', description: 'Trend reversal indicator', parameters: ['fast', 'slow'] },
  { id: 'ulcer_index', name: 'Ulcer Index', shortName: 'ULCER', category: 'volatility', icon: <Activity size={16} />, defaultParams: { length: 14 }, color: '#dc2626', description: 'Downside volatility', parameters: ['length'] },

  // ================ VOLUME (18) ================
  { id: 'obv', name: 'On Balance Volume', shortName: 'OBV', category: 'volume', icon: <BarChart size={16} />, defaultParams: {}, color: '#64748b', description: 'Cumulative volume indicator', parameters: [] },
  { id: 'volume_profile', name: 'Volume Profile', shortName: 'VP', category: 'volume', icon: <PieChart size={16} />, defaultParams: { bins: 100 }, color: '#0ea5e9', description: 'Volume by price levels', parameters: ['bins'] },
  { id: 'ad', name: 'Accumulation/Distribution', shortName: 'AD', category: 'volume', icon: <BarChart size={16} />, defaultParams: { length: 14 }, color: '#2563eb', description: 'Money flow volume', parameters: ['length'] },
  { id: 'cmf', name: 'Chaikin Money Flow', shortName: 'CMF', category: 'volume', icon: <BarChart size={16} />, defaultParams: { length: 20 }, color: '#059669', description: 'Money flow direction', parameters: ['length'] },
  { id: 'emv', name: 'Ease of Movement', shortName: 'EMV', category: 'volume', icon: <Waves size={16} />, defaultParams: { length: 14 }, color: '#0891b2', description: 'Volume/price relationship', parameters: ['length'] },
  { id: 'vpt', name: 'Volume Price Trend', shortName: 'VPT', category: 'volume', icon: <TrendingUp size={16} />, defaultParams: {}, color: '#ca8a04', description: 'Price-volume trend', parameters: [] },
  { id: 'nvi', name: 'Negative Volume Index', shortName: 'NVI', category: 'volume', icon: <TrendingDown size={16} />, defaultParams: {}, color: '#dc2626', description: 'Smart money indicator', parameters: [] },
  { id: 'pvi', name: 'Positive Volume Index', shortName: 'PVI', category: 'volume', icon: <TrendingUp size={16} />, defaultParams: {}, color: '#22c55e', description: 'Volume momentum', parameters: [] },
  { id: 'force_index', name: 'Force Index', shortName: 'FORCE', category: 'volume', icon: <Activity size={16} />, defaultParams: { length: 13 }, color: '#8b5cf6', description: 'Buying/selling pressure', parameters: ['length'] },
  { id: 'klinger_vol', name: 'Klinger Volume Oscillator', shortName: 'KVOL', category: 'volume', icon: <Activity size={16} />, defaultParams: { fast: 34, slow: 55 }, color: '#f97316', description: 'Volume oscillator', parameters: ['fast', 'slow'] },
  { id: 'volume_roc', name: 'Volume Rate of Change', shortName: 'V_ROC', category: 'volume', icon: <TrendingUp size={16} />, defaultParams: { length: 14 }, color: '#06b6d4', description: 'Volume momentum', parameters: ['length'] },
  { id: 'vwap_daily', name: 'VWAP (Daily)', shortName: 'VWAP_D', category: 'volume', icon: <BarChart2 size={16} />, defaultParams: {}, color: '#8b5cf6', description: 'Daily VWAP', parameters: [] },
  { id: 'vwap_session', name: 'VWAP (Session)', shortName: 'VWAP_S', category: 'volume', icon: <BarChart2 size={16} />, defaultParams: {}, color: '#ec4899', description: 'Session VWAP', parameters: [] },
  { id: 'ad_line', name: 'AD Line', shortName: 'AD_LINE', category: 'volume', icon: <TrendingUp size={16} />, defaultParams: {}, color: '#14b8a6', description: 'Accumulation/distribution line', parameters: [] },
  { id: 'mfi_raw', name: 'Money Flow Index (Raw)', shortName: 'MFI_RAW', category: 'volume', icon: <BarChart size={16} />, defaultParams: { length: 14 }, color: '#ca8a04', description: 'Raw money flow', parameters: ['length'] },
  { id: 'trade_volume_index', name: 'Trade Volume Index', shortName: 'TVI', category: 'volume', icon: <BarChart size={16} />, defaultParams: { min_tick: 0.01 }, color: '#64748b', description: 'Volume-weighted price', parameters: ['min_tick'] },
  { id: 'volume_delta', name: 'Volume Delta', shortName: 'V_DELTA', category: 'volume', icon: 'Δ', defaultParams: {}, color: '#22c55e', description: 'Net buy/sell volume', parameters: [] },

  // ================ PIVOTS & FIBONACCI (8) ================
  { id: 'fibonacci_retracement', name: 'Fibonacci Retracement', shortName: 'FIB_RETR', category: 'pivots', icon: <LineChart size={16} />, defaultParams: { levels: [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1] }, color: '#10b981', description: 'Support/resistance levels', parameters: ['levels'] },
  { id: 'fibonacci_extension', name: 'Fibonacci Extension', shortName: 'FIB_EXT', category: 'pivots', icon: <LineChart size={16} />, defaultParams: { levels: [0, 0.618, 1, 1.618, 2.618, 3.618] }, color: '#06b6d4', description: 'Price projection levels', parameters: ['levels'] },
  { id: 'pivot_classic_r', name: 'Pivot Classic R1', shortName: 'PIV_R1', category: 'pivots', icon: <TrendingUp size={16} />, defaultParams: {}, color: '#ef4444', description: 'Classic pivot R1', parameters: [] },
  { id: 'pivot_classic_s', name: 'Pivot Classic S1', shortName: 'PIV_S1', category: 'pivots', icon: <TrendingDown size={16} />, defaultParams: {}, color: '#22c55e', description: 'Classic pivot S1', parameters: [] },
  { id: 'pivot_camarilla_r', name: 'Pivot Camarilla R1', shortName: 'CAMA_R1', category: 'pivots', icon: <TrendingUp size={16} />, defaultParams: {}, color: '#f97316', description: 'Camarilla R1', parameters: [] },
  { id: 'pivot_camarilla_s', name: 'Pivot Camarilla S1', shortName: 'CAMA_S1', category: 'pivots', icon: <TrendingDown size={16} />, defaultParams: {}, color: '#14b8a6', description: 'Camarilla S1', parameters: [] },
  { id: 'pivot_fib_r', name: 'Pivot Fibonacci R1', shortName: 'FIB_PIV_R1', category: 'pivots', icon: <TrendingUp size={16} />, defaultParams: {}, color: '#8b5cf6', description: 'Fibonacci pivot R1', parameters: [] },
  { id: 'pivot_fib_s', name: 'Pivot Fibonacci S1', shortName: 'FIB_PIV_S1', category: 'pivots', icon: <TrendingDown size={16} />, defaultParams: {}, color: '#ec4899', description: 'Fibonacci pivot S1', parameters: [] },

  // ================ ICHIMOKU COMPONENTS (5) ================
  { id: 'tenkan_sen', name: 'Tenkan-Sen (Conversion)', shortName: 'TENKAN', category: 'ichimoku', icon: <TrendingUp size={16} />, defaultParams: { length: 9 }, color: '#ef4444', description: 'Ichimoku conversion line', parameters: ['length'] },
  { id: 'kijun_sen', name: 'Kijun-Sen (Base)', shortName: 'KIJUN', category: 'ichimoku', icon: <TrendingUp size={16} />, defaultParams: { length: 26 }, color: '#3b82f6', description: 'Ichimoku base line', parameters: ['length'] },
  { id: 'senkou_a', name: 'Senkou Span A', shortName: 'SENKOU_A', category: 'ichimoku', icon: <Waves size={16} />, defaultParams: { tenkan: 9, kijun: 26 }, color: '#22c55e', description: 'Leading span A', parameters: ['tenkan', 'kijun'] },
  { id: 'senkou_b', name: 'Senkou Span B', shortName: 'SENKOU_B', category: 'ichimoku', icon: <Waves size={16} />, defaultParams: { length: 52 }, color: '#ef4444', description: 'Leading span B', parameters: ['length'] },
  { id: 'chikou_span', name: 'Chikou Span (Lagging)', shortName: 'CHIKOU', category: 'ichimoku', icon: <TrendingDown size={16} />, defaultParams: { length: 26 }, color: '#8b5cf6', description: 'Lagging span', parameters: ['length'] },

  // ================ AROON COMPONENTS (3) ================
  { id: 'aroon_up', name: 'Aroon Up', shortName: 'AROON_UP', category: 'aroon', icon: <TrendingUp size={16} />, defaultParams: { length: 25 }, color: '#22c55e', description: 'Aroon upward line', parameters: ['length'] },
  { id: 'aroon_down', name: 'Aroon Down', shortName: 'AROON_DOWN', category: 'aroon', icon: <TrendingDown size={16} />, defaultParams: { length: 25 }, color: '#ef4444', description: 'Aroon downward line', parameters: ['length'] },
  { id: 'aroon_oscillator', name: 'Aroon Oscillator', shortName: 'AROON_OSC', category: 'aroon', icon: <Activity size={16} />, defaultParams: { length: 25 }, color: '#8b5cf6', description: 'Aroon difference', parameters: ['length'] },

  // ================ STOCHASTIC COMPONENTS (3) ================
  { id: 'stoch_k', name: 'Stochastic %K', shortName: 'STOCH_K', category: 'stochastic', icon: <Activity size={16} />, defaultParams: { k: 14, smooth: 1 }, color: '#3b82f6', description: 'Stochastic %K line', parameters: ['k', 'smooth'] },
  { id: 'stoch_d', name: 'Stochastic %D', shortName: 'STOCH_D', category: 'stochastic', icon: <Activity size={16} />, defaultParams: { k: 14, d: 3 }, color: '#f59e0b', description: 'Stochastic %D line', parameters: ['k', 'd'] },
  { id: 'stoch_rsi_k', name: 'StochRSI %K', shortName: 'SRSI_K', category: 'stochastic', icon: <Activity size={16} />, defaultParams: { length: 14, k: 3 }, color: '#22c55e', description: 'StochRSI %K line', parameters: ['length', 'k'] },

  // ================ MACD COMPONENTS (4) ================
  { id: 'macd_line', name: 'MACD Line', shortName: 'MACD_LINE', category: 'macd', icon: <BarChart2 size={16} />, defaultParams: { fast: 12, slow: 26 }, color: '#06b6d4', description: 'MACD main line', parameters: ['fast', 'slow'] },
  { id: 'macd_signal', name: 'MACD Signal', shortName: 'MACD_SIG', category: 'macd', icon: <Activity size={16} />, defaultParams: { fast: 12, slow: 26, signal: 9 }, color: '#f97316', description: 'MACD signal line', parameters: ['fast', 'slow', 'signal'] },
  { id: 'macd_histogram', name: 'MACD Histogram', shortName: 'MACD_HIST', category: 'macd', icon: <BarChart size={16} />, defaultParams: { fast: 12, slow: 26, signal: 9 }, color: '#8b5cf6', description: 'MACD histogram', parameters: ['fast', 'slow', 'signal'] },
  { id: 'macd_cross', name: 'MACD Crossover', shortName: 'MACD_CROSS', category: 'macd', icon: <Crosshair size={16} />, defaultParams: { fast: 12, slow: 26, signal: 9 }, color: '#22c55e', description: 'MACD signal crossover', parameters: ['fast', 'slow', 'signal'] },

  // ================ RSI COMPONENTS (3) ================
  { id: 'rsi_raw', name: 'RSI Raw', shortName: 'RSI_RAW', category: 'rsi', icon: <Activity size={16} />, defaultParams: { length: 14, source: 'close' }, color: '#f59e0b', description: 'Raw RSI value', parameters: ['length', 'source'] },
  { id: 'rsi_overbought', name: 'RSI Overbought Line', shortName: 'RSI_OB', category: 'rsi', icon: <TrendingUp size={16} />, defaultParams: { level: 70 }, color: '#ef4444', description: 'Overbought threshold', parameters: ['level'] },
  { id: 'rsi_oversold', name: 'RSI Oversold Line', shortName: 'RSI_OS', category: 'rsi', icon: <TrendingDown size={16} />, defaultParams: { level: 30 }, color: '#22c55e', description: 'Oversold threshold', parameters: ['level'] },

  // ================ SUPERTREND COMPONENTS (3) ================
  { id: 'supertrend_upper', name: 'Supertrend Upper', shortName: 'ST_UPPER', category: 'supertrend', icon: <TrendingDown size={16} />, defaultParams: { period: 10, multiplier: 3 }, color: '#ef4444', description: 'Upper Supertrend line', parameters: ['period', 'multiplier'] },
  { id: 'supertrend_lower', name: 'Supertrend Lower', shortName: 'ST_LOWER', category: 'supertrend', icon: <TrendingUp size={16} />, defaultParams: { period: 10, multiplier: 3 }, color: '#22c55e', description: 'Lower Supertrend line', parameters: ['period', 'multiplier'] },
  { id: 'supertrend_direction', name: 'Supertrend Direction', shortName: 'ST_DIR', category: 'supertrend', icon: <Activity size={16} />, defaultParams: { period: 10, multiplier: 3 }, color: '#8b5cf6', description: 'Trend direction signal', parameters: ['period', 'multiplier'] },

  // ================ CUSTOM/PRICE (5) ================
  { id: 'price', name: 'Price', shortName: 'PRICE', category: 'custom', icon: <Target size={16} />, defaultParams: {}, color: '#10b981', description: 'Current price', parameters: [] },
  { id: 'volume_raw', name: 'Volume', shortName: 'VOL', category: 'custom', icon: <Layers size={16} />, defaultParams: { multiplier: 1.5 }, color: '#64748b', description: 'Trading volume', parameters: ['multiplier'] },
  { id: 'candle', name: 'Candle Pattern', shortName: 'CANDLE', category: 'custom', icon: <BarChart2 size={16} />, defaultParams: { threshold: 0.1 }, color: '#22c55e', description: 'Candlestick patterns', parameters: ['threshold'] },
  { id: 'open', name: 'Open Price', shortName: 'OPEN', category: 'custom', icon: <Target size={16} />, defaultParams: {}, color: '#3b82f6', description: 'Open price', parameters: [] },
  { id: 'high', name: 'High Price', shortName: 'HIGH', category: 'custom', icon: <TrendingUp size={16} />, defaultParams: {}, color: '#22c55e', description: 'High price', parameters: [] },
  { id: 'low', name: 'Low Price', shortName: 'LOW', category: 'custom', icon: <TrendingDown size={16} />, defaultParams: {}, color: '#ef4444', description: 'Low price', parameters: [] },
  { id: 'close', name: 'Close Price', shortName: 'CLOSE', category: 'custom', icon: <Target size={16} />, defaultParams: {}, color: '#8b5cf6', description: 'Close price', parameters: [] },
  { id: 'hl2', name: 'High-Low Avg', shortName: 'HL2', category: 'custom', icon: <LineChart size={16} />, defaultParams: {}, color: '#64748b', description: '(High+Low)/2', parameters: [] },
  { id: 'hlc3', name: 'HLC Avg', shortName: 'HLC3', category: 'custom', icon: <LineChart size={16} />, defaultParams: {}, color: '#f59e0b', description: '(High+Low+Close)/3', parameters: [] },
  { id: 'ohlc4', name: 'OHLC Avg', shortName: 'OHLC4', category: 'custom', icon: <LineChart size={16} />, defaultParams: {}, color: '#06b6d4', description: '(Open+High+Low+Close)/4', parameters: [] },
]

// =============================================================================
// ALL 163 COMPARISON OPERATORS (from schemas/__init__.py - AVAILABLE_OPERATORS)
// =============================================================================

// =============================================================================
// ALL 163 COMPARISON OPERATORS (from schemas/__init__.py - AVAILABLE_OPERATORS)
// =============================================================================
const OPERATORS: Operator[] = [
  // ========== BASIC COMPARISON ==========
  { id: 'gt', name: 'Greater Than', symbol: '>', description: 'Is greater than', requiresValue: true },
  { id: 'lt', name: 'Less Than', symbol: '<', description: 'Is less than', requiresValue: true },
  { id: 'gte', name: 'Greater or Equal', symbol: '≥', description: 'Greater than or equal to', requiresValue: true },
  { id: 'lte', name: 'Less or Equal', symbol: '≤', description: 'Less than or equal to', requiresValue: true },
  { id: 'eq', name: 'Equals', symbol: '=', description: 'Is equal to', requiresValue: true },
  { id: 'ne', name: 'Not Equals', symbol: '≠', description: 'Is not equal to', requiresValue: true },
  { id: 'between', name: 'Between', symbol: '↔', description: 'Is between two values', requiresValue: false, requiresIndicator: true },
  { id: 'not_between', name: 'Not Between', symbol: '↮', description: 'Is not between two values', requiresValue: false },
  { id: 'in_range', name: 'In Range', symbol: '∋', description: 'Value is within range', requiresValue: false },
  { id: 'outside_range', name: 'Outside Range', symbol: '∌', description: 'Value is outside range', requiresValue: false },
  
  // ========== CROSSOVER OPERATORS ==========
  { id: 'crosses_above', name: 'Crosses Above', symbol: '↗', description: 'Crosses above value (crossover)', requiresValue: false },
  { id: 'crosses_below', name: 'Crosses Below', symbol: '↘', description: 'Crosses below value (crossunder)', requiresValue: false },
  { id: 'crosses', name: 'Crosses', symbol: '↕', description: 'Crosses value', requiresValue: false },
  { id: 'value_crosses', name: 'Value Crosses', symbol: '⟷', description: 'Value crosses reference', requiresValue: false },
  { id: 'value_crosses_up', name: 'Value Crosses Up', symbol: '↑', description: 'Value crosses upward', requiresValue: false },
  { id: 'value_crosses_down', name: 'Value Crosses Down', symbol: '↓', description: 'Value crosses downward', requiresValue: false },
  
  // ========== POSITION OPERATORS ==========
  { id: 'is_above', name: 'Is Above', symbol: '↑', description: 'Is above threshold', requiresValue: true },
  { id: 'is_below', name: 'Is Below', symbol: '↓', description: 'Is below threshold', requiresValue: true },
  { id: 'is_equal', name: 'Is Equal', symbol: '=', description: 'Is equal to', requiresValue: true },
  { id: 'pct_above', name: 'Percent Above', symbol: '%↑', description: 'Percentage above', requiresValue: true },
  { id: 'pct_below', name: 'Percent Below', symbol: '%↓', description: 'Percentage below', requiresValue: true },
  { id: 'pct_change_gt', name: 'Pct Change >', symbol: 'Δ%', description: 'Percent change greater', requiresValue: true },
  { id: 'pct_change_lt', name: 'Pct Change <', symbol: 'Δ%↔', description: 'Percent change less', requiresValue: true },
  { id: 'volatility_above', name: 'Volatility Above', symbol: 'σ↑', description: 'Volatility above', requiresValue: true },
  { id: 'volatility_below', name: 'Volatility Below', symbol: 'σ↓', description: 'Volatility below', requiresValue: true },
  { id: 'std_dev_above', name: 'Std Dev Above', symbol: 'σ+', description: 'Standard deviation above', requiresValue: true },
  { id: 'std_dev_below', name: 'Std Dev Below', symbol: 'σ-', description: 'Standard deviation below', requiresValue: true },
  
  // ========== TIME-BASED OPERATORS ==========
  { id: 'time_in_session', name: 'Time In Session', symbol: '⏰', description: 'Within time session', requiresValue: false },
  { id: 'time_out_session', name: 'Time Out Session', symbol: '⏱', description: 'Outside time session', requiresValue: false },
  { id: 'weekday_is', name: 'Weekday Is', symbol: '📅', description: 'Specific weekday', requiresValue: true },
  { id: 'month_is', name: 'Month Is', symbol: '📆', description: 'Specific month', requiresValue: true },
  { id: 'volume_above_avg', name: 'Volume Above Avg', symbol: 'V↑', description: 'Volume above average', requiresValue: true },
  { id: 'volume_below_avg', name: 'Volume Below Avg', symbol: 'V↓', description: 'Volume below average', requiresValue: true },
  { id: 'volume_spike', name: 'Volume Spike', symbol: 'V!', description: 'Volume spike detected', requiresValue: true },
  { id: 'obv_above', name: 'OBV Above', symbol: 'OBV↑', description: 'On-balance volume above', requiresValue: true },
  { id: 'obv_crosses_above', name: 'OBV Crosses Above', symbol: 'OBV↗', description: 'OBV crosses above', requiresValue: false },
  
  // ========== MOMENTUM OPERATORS ==========
  { id: 'momentum_above', name: 'Momentum Above', symbol: 'M↑', description: 'Momentum above threshold', requiresValue: true },
  { id: 'momentum_below', name: 'Momentum Below', symbol: 'M↓', description: 'Momentum below threshold', requiresValue: true },
  { id: 'momentum_positive', name: 'Momentum Positive', symbol: 'M+', description: 'Positive momentum', requiresValue: false },
  { id: 'momentum_negative', name: 'Momentum Negative', symbol: 'M-', description: 'Negative momentum', requiresValue: false },
  { id: 'rsi_above', name: 'RSI Above', symbol: 'RSI↑', description: 'RSI above threshold', requiresValue: true },
  { id: 'rsi_below', name: 'RSI Below', symbol: 'RSI↓', description: 'RSI below threshold', requiresValue: true },
  { id: 'rsi_overbought', name: 'RSI Overbought', symbol: 'RSI>70', description: 'RSI overbought (>70)', requiresValue: false },
  { id: 'rsi_oversold', name: 'RSI Oversold', symbol: 'RSI<30', description: 'RSI oversold (<30)', requiresValue: false },
  { id: 'trend_above', name: 'Trend Above', symbol: 'T↑', description: 'Trend above', requiresValue: true },
  { id: 'trend_below', name: 'Trend Below', symbol: 'T↓', description: 'Trend below', requiresValue: true },
  { id: 'ema_alignment_up', name: 'EMA Alignment Up', symbol: 'EMA↗', description: 'EMAs aligned upward', requiresValue: false },
  { id: 'ema_alignment_down', name: 'EMA Alignment Down', symbol: 'EMA↘', description: 'EMAs aligned downward', requiresValue: false },
  { id: 'price_above_ema', name: 'Price Above EMA', symbol: 'P>EMA', description: 'Price above EMA', requiresValue: false },
  { id: 'price_below_ema', name: 'Price Below EMA', symbol: 'P<EMA', description: 'Price below EMA', requiresValue: false },
  { id: 'price_above_sma', name: 'Price Above SMA', symbol: 'P>SMA', description: 'Price above SMA', requiresValue: false },
  { id: 'price_below_sma', name: 'Price Below SMA', symbol: 'P<SMA', description: 'Price below SMA', requiresValue: false },
  
  // ========== PATTERN OPERATORS ==========
  { id: 'pattern_bullish', name: 'Pattern Bullish', symbol: '🐂', description: 'Bullish pattern detected', requiresValue: false },
  { id: 'pattern_bearish', name: 'Pattern Bearish', symbol: '🐻', description: 'Bearish pattern detected', requiresValue: false },
  { id: 'pattern_doji', name: 'Pattern Doji', symbol: '+', description: 'Doji pattern', requiresValue: false },
  { id: 'pattern_hammer', name: 'Pattern Hammer', symbol: '🔨', description: 'Hammer pattern', requiresValue: false },
  { id: 'pattern_engulf', name: 'Pattern Engulf', symbol: '🥁', description: 'Engulfing pattern', requiresValue: false },
  { id: 'contains', name: 'Contains', symbol: '∈', description: 'Contains value', requiresValue: true },
  { id: 'not_contains', name: 'Not Contains', symbol: '∉', description: 'Does not contain', requiresValue: true },
  { id: 'starts_with', name: 'Starts With', symbol: 'S→', description: 'Starts with', requiresValue: true },
  { id: 'ends_with', name: 'Ends With', symbol: '→E', description: 'Ends with', requiresValue: true },
  { id: 'regex_match', name: 'Regex Match', symbol: '.*', description: 'Regex pattern match', requiresValue: true },
  { id: 'not_regex_match', name: 'Not Regex Match', symbol: '!.*', description: 'Not regex match', requiresValue: true },
  { id: 'signal_is', name: 'Signal Is', symbol: 'SIG=', description: 'Signal equals', requiresValue: true },
  { id: 'signal_in', name: 'Signal In', symbol: 'SIG∈', description: 'Signal in list', requiresValue: true },
  { id: 'action_is', name: 'Action Is', symbol: 'ACT=', description: 'Action equals', requiresValue: true },
  
  // ========== BOLLINGER BAND OPERATORS ==========
  { id: 'bb_upper_touch', name: 'BB Upper Touch', symbol: 'BB↗', description: 'Bollinger upper touch', requiresValue: false },
  { id: 'bb_lower_touch', name: 'BB Lower Touch', symbol: 'BB↘', description: 'Bollinger lower touch', requiresValue: false },
  { id: 'bb_middle_touch', name: 'BB Middle Touch', symbol: 'BB-', description: 'Bollinger middle touch', requiresValue: false },
  { id: 'bb_expansion', name: 'BB Expansion', symbol: 'BB!', description: 'Bollinger expansion', requiresValue: false },
  { id: 'bb_squeeze', name: 'BB Squeeze', symbol: 'BB<>', description: 'Bollinger squeeze', requiresValue: false },
  { id: 'at_fib_level', name: 'At Fib Level', symbol: 'FIB', description: 'At Fibonacci level', requiresValue: true },
  { id: 'near_fib_level', name: 'Near Fib Level', symbol: '~FIB', description: 'Near Fibonacci level', requiresValue: true },
  { id: 'near_support', name: 'Near Support', symbol: 'SPRT', description: 'Near support level', requiresValue: false },
  { id: 'near_resistance', name: 'Near Resistance', symbol: 'RES', description: 'Near resistance', requiresValue: false },
  
  // ========== ADVANCED OPERATORS ==========
  { id: 'donchian_breakout', name: 'Donchian Breakout', symbol: 'DC↗', description: 'Donchian breakout', requiresValue: false },
  { id: 'donchian_breakdown', name: 'Donchian Breakdown', symbol: 'DC↘', description: 'Donchian breakdown', requiresValue: false },
  { id: 'keltner_breakout', name: 'Keltner Breakout', symbol: 'KC↗', description: 'Keltner breakout', requiresValue: false },
  { id: 'divergence_bullish', name: 'Divergence Bullish', symbol: 'DIV↗', description: 'Bullish divergence', requiresValue: false },
  { id: 'divergence_bearish', name: 'Divergence Bearish', symbol: 'DIV↘', description: 'Bearish divergence', requiresValue: false },
  { id: 'macd_hist_positive', name: 'MACD Hist +', symbol: 'MHD+', description: 'MACD histogram positive', requiresValue: false },
  { id: 'macd_hist_negative', name: 'MACD Hist -', symbol: 'MHD-', description: 'MACD histogram negative', requiresValue: false },
  { id: 'macd_cross_up', name: 'MACD Cross Up', symbol: 'MAC↗', description: 'MACD cross up', requiresValue: false },
  { id: 'macd_cross_down', name: 'MACD Cross Down', symbol: 'MAC↘', description: 'MACD cross down', requiresValue: false },
  
  // ========== LOGICAL OPERATORS ==========
  { id: 'and', name: 'AND', symbol: '∧', description: 'Logical AND', requiresValue: false },
  { id: 'or', name: 'OR', symbol: '∨', description: 'Logical OR', requiresValue: false },
  { id: 'xor', name: 'XOR', symbol: '⊕', description: 'Logical XOR', requiresValue: false },
  { id: 'nand', name: 'NAND', symbol: '⊼', description: 'Logical NAND', requiresValue: false },
  { id: 'custom_formula', name: 'Custom Formula', symbol: 'f(x)', description: 'Custom formula', requiresValue: true },
  { id: 'webhook_match', name: 'Webhook Match', symbol: '⟲', description: 'Webhook match', requiresValue: true },
  { id: 'json_path_match', name: 'JSON Path Match', symbol: '{}=', description: 'JSON path match', requiresValue: true },
  
  // ========== GREEK OPERATORS (OPTIONS) ==========
  { id: 'delta_above', name: 'Delta Above', symbol: 'Δ↑', description: 'Delta above threshold', requiresValue: true },
  { id: 'delta_below', name: 'Delta Below', symbol: 'Δ↓', description: 'Delta below threshold', requiresValue: true },
  { id: 'delta_near_zero', name: 'Delta Near Zero', symbol: 'Δ≈0', description: 'Delta near zero', requiresValue: false },
  { id: 'delta_near_one', name: 'Delta Near 1', symbol: 'Δ≈1', description: 'Delta near one', requiresValue: false },
  { id: 'delta_near_half', name: 'Delta Near 0.5', symbol: 'Δ≈0.5', description: 'Delta near half', requiresValue: false },
  { id: 'gamma_above', name: 'Gamma Above', symbol: 'Γ↑', description: 'Gamma above threshold', requiresValue: true },
  { id: 'gamma_below', name: 'Gamma Below', symbol: 'Γ↓', description: 'Gamma below threshold', requiresValue: true },
  { id: 'gamma_high', name: 'Gamma High', symbol: 'Γ!', description: 'Gamma high', requiresValue: false },
  { id: 'theta_above', name: 'Theta Above', symbol: 'Θ↑', description: 'Theta above threshold', requiresValue: true },
  { id: 'theta_below', name: 'Theta Below', symbol: 'Θ↓', description: 'Theta below threshold', requiresValue: true },
  { id: 'theta_accelerating', name: 'Theta Accelerating', symbol: 'Θ!!', description: 'Theta accelerating', requiresValue: false },
  { id: 'theta_zero_day', name: 'Theta Zero Day', symbol: 'Θ0', description: 'Theta zero day', requiresValue: false },
  { id: 'vega_above', name: 'Vega Above', symbol: 'V↑', description: 'Vega above threshold', requiresValue: true },
  { id: 'vega_below', name: 'Vega Below', symbol: 'V↓', description: 'Vega below threshold', requiresValue: true },
  { id: 'vega_sensitivity_high', name: 'Vega Sensitivity', symbol: 'V!', description: 'High vega sensitivity', requiresValue: false },
  
  // ========== IV (IMPLIED VOLATILITY) OPERATORS ==========
  { id: 'iv_above', name: 'IV Above', symbol: 'IV↑', description: 'Implied volatility above', requiresValue: true },
  { id: 'iv_below', name: 'IV Below', symbol: 'IV↓', description: 'Implied volatility below', requiresValue: true },
  { id: 'iv_expanding', name: 'IV Expanding', symbol: 'IV↗', description: 'IV expanding', requiresValue: false },
  { id: 'iv_contracting', name: 'IV Contracting', symbol: 'IV↘', description: 'IV contracting', requiresValue: false },
  { id: 'iv_rank_high', name: 'IV Rank High', symbol: 'IVR!', description: 'High IV rank', requiresValue: false },
  { id: 'iv_rank_low', name: 'IV Rank Low', symbol: 'IVR↔', description: 'Low IV rank', requiresValue: false },
  { id: 'iv_higher_than_hv', name: 'IV > HV', symbol: 'IV>HV', description: 'IV higher than HV', requiresValue: false },
  { id: 'iv_lower_than_hv', name: 'IV < HV', symbol: 'IV<HV', description: 'IV lower than HV', requiresValue: false },
  
  // ========== PREMIUM & OPTIONS OPERATORS ==========
  { id: 'premium_above', name: 'Premium Above', symbol: 'PREM↑', description: 'Premium above', requiresValue: true },
  { id: 'premium_below', name: 'Premium Below', symbol: 'PREM↓', description: 'Premium below', requiresValue: true },
  { id: 'premium_spike', name: 'Premium Spike', symbol: 'PREM!', description: 'Premium spike', requiresValue: true },
  { id: 'premium_cheap', name: 'Premium Cheap', symbol: 'PREM$', description: 'Premium cheap', requiresValue: false },
  { id: 'premium_expensive', name: 'Premium Expensive', symbol: 'PREM$$', description: 'Premium expensive', requiresValue: false },
  { id: 'strike_above_spot', name: 'Strike Above Spot', symbol: 'K↑', description: 'Strike above spot', requiresValue: false },
  { id: 'strike_below_spot', name: 'Strike Below Spot', symbol: 'K↓', description: 'Strike below spot', requiresValue: false },
  { id: 'strike_atm', name: 'Strike ATM', symbol: 'K≈', description: 'At-the-money strike', requiresValue: false },
  { id: 'strike_itm', name: 'Strike ITM', symbol: 'K>SP', description: 'In-the-money strike', requiresValue: false },
  { id: 'strike_otm', name: 'Strike OTM', symbol: 'K<SP', description: 'Out-of-the-money strike', requiresValue: false },
  { id: 'strike_near_strike', name: 'Strike Near Strike', symbol: 'K≈K', description: 'Near specific strike', requiresValue: true },
  { id: 'moneyness_above', name: 'Moneyness Above', symbol: 'M↑', description: 'Moneyness above', requiresValue: true },
  { id: 'moneyness_below', name: 'Moneyness Below', symbol: 'M↓', description: 'Moneyness below', requiresValue: true },
  
  // ========== DTE (DAYS TO EXPIRY) OPERATORS ==========
  { id: 'dte_above', name: 'DTE Above', symbol: 'DTE↑', description: 'Days to expiry above', requiresValue: true },
  { id: 'dte_below', name: 'DTE Below', symbol: 'DTE↓', description: 'Days to expiry below', requiresValue: true },
  { id: 'dte_zero', name: 'DTE Zero', symbol: 'DTE0', description: 'Expiry day', requiresValue: false },
  { id: 'dte_weeklies', name: 'DTE Weeklies', symbol: 'DTE7', description: 'Weekly expiry', requiresValue: false },
  { id: 'dte_monthlies', name: 'DTE Monthlies', symbol: 'DTE30', description: 'Monthly expiry', requiresValue: false },
  
  // ========== PCR (PUT CALL RATIO) OPERATORS ==========
  { id: 'pcr_above', name: 'PCR Above', symbol: 'PCR↑', description: 'PCR above threshold', requiresValue: true },
  { id: 'pcr_below', name: 'PCR Below', symbol: 'PCR↓', description: 'PCR below threshold', requiresValue: true },
  { id: 'pcr_extreme_high', name: 'PCR Extreme High', symbol: 'PCR!↑', description: 'PCR extremely high', requiresValue: false },
  { id: 'pcr_extreme_low', name: 'PCR Extreme Low', symbol: 'PCR!↓', description: 'PCR extremely low', requiresValue: false },
  
  // ========== OI (OPEN INTEREST) OPERATORS ==========
  { id: 'oi_spike', name: 'OI Spike', symbol: 'OI!↑', description: 'Open interest spike', requiresValue: true },
  { id: 'oi_drop', name: 'OI Drop', symbol: 'OI!↓', description: 'Open interest drop', requiresValue: true },
  { id: 'oi_concentration', name: 'OI Concentration', symbol: 'OI%', description: 'OI concentration', requiresValue: true },
  
  // ========== SPREAD OPERATORS ==========
  { id: 'spread_widening', name: 'Spread Widening', symbol: 'SP↗', description: 'Spread widening', requiresValue: false },
  { id: 'spread_narrowing', name: 'Spread Narrowing', symbol: 'SP↘', description: 'Spread narrowing', requiresValue: false },
  { id: 'spread_above_pct', name: 'Spread Above %', symbol: 'SP%↑', description: 'Spread above percentage', requiresValue: true },
  { id: 'spread_below_pct', name: 'Spread Below %', symbol: 'SP%↓', description: 'Spread below percentage', requiresValue: true },
  
  // ========== VOLUME OPERATORS ==========
  { id: 'volume_dry', name: 'Volume Dry', symbol: 'VDRY', description: 'Volume dry', requiresValue: false },
  { id: 'volume_call_spike', name: 'Volume Call Spike', symbol: 'VCALL!', description: 'Call volume spike', requiresValue: true },
  { id: 'volume_put_spike', name: 'Volume Put Spike', symbol: 'VPUT!', description: 'Put volume spike', requiresValue: true },
  
  // ========== MAX PAIN OPERATORS ==========
  { id: 'price_near_max_pain', name: 'Near Max Pain', symbol: 'MP≈', description: 'Near max pain price', requiresValue: true },
  { id: 'price_away_max_pain', name: 'Away Max Pain', symbol: 'MP↔', description: 'Away from max pain', requiresValue: true },
  
  // ========== TERM STRUCTURE OPERATORS ==========
  { id: 'skew_steep', name: 'Skew Steep', symbol: 'SKW↗', description: 'Skew steepening', requiresValue: false },
  { id: 'skew_flat', name: 'Skew Flat', symbol: 'SKW→', description: 'Skew flattening', requiresValue: false },
  { id: 'skew_reversing', name: 'Skew Reversing', symbol: 'SKW⟲', description: 'Skew reversing', requiresValue: false },
  { id: 'term_contango', name: 'Term Contango', symbol: 'TERM→', description: 'Term structure contango', requiresValue: false },
  { id: 'term_backwardation', name: 'Term Backwardation', symbol: 'TERM←', description: 'Term structure backwardation', requiresValue: false },
  { id: 'term_flat', name: 'Term Flat', symbol: 'TERM=', description: 'Term structure flat', requiresValue: false },
  
  // ========== GREEK EXPOSURE OPERATORS ==========
  { id: 'net_delta', name: 'Net Delta', symbol: 'ΔNET', description: 'Net delta exposure', requiresValue: true },
  { id: 'net_gamma', name: 'Net Gamma', symbol: 'ΓNET', description: 'Net gamma exposure', requiresValue: true },
  { id: 'net_theta', name: 'Net Theta', symbol: 'ΘNET', description: 'Net theta decay', requiresValue: true },
  { id: 'net_vega', name: 'Net Vega', symbol: 'VNET', description: 'Net vega exposure', requiresValue: true },
  
  // ========== ICHIMOKU OPERATORS ==========
  { id: 'Ichimoku_A_above_B', name: 'Ichimoku A>B', symbol: 'IA>B', description: 'Ichimoku A above B (Tenkan > Kijun)', requiresValue: false },
  { id: 'Ichimoku_A_below_B', name: 'Ichimoku A<B', symbol: 'IA<B', description: 'Ichimoku A below B (Tenkan < Kijun)', requiresValue: false },
  
  // ========== ITM/OTM OPERATORS ==========
  { id: 'itmq', name: 'ITM Quadrant', symbol: 'ITMQ', description: 'In-the-money quadrant (Delta > 0.5)', requiresValue: false },
  { id: 'otmq', name: 'OTM Quadrant', symbol: 'OTMQ', description: 'Out-of-the-money quadrant (Delta < 0.5)', requiresValue: false },
]

// =============================================================================
// KEYWORD FUNCTIONS CATEGORIES (from keywords.py - 325+ total)
// =============================================================================

const KEYWORD_CATEGORIES = {
  price: ['close', 'open', 'high', 'low', 'volume', 'price', 'price_change', 'price_change_pct', 'price_range', 'price_range_pct', 'vwap', 'vwap_distance'],
  candle: ['candle_color', 'is_bullish', 'is_bearish', 'is_doji'],
  indicator: ['sma', 'ema', 'wma', 'hma', 'dema', 'tema', 'adx', 'adx_strong', 'adx_weak', 'rsi', 'rsi_overbought', 'rsi_oversold', 'stoch_k', 'stoch_d', 'macd', 'macd_signal', 'macd_hist', 'cci', 'momentum', 'roc', 'williams_r', 'atr', 'bb_upper', 'bb_middle', 'bb_lower', 'bb_width', 'bb_squeeze', 'keltner_upper', 'keltner_lower', 'obv', 'ad_line', 'mfi', 'cmf', 'supertrend', 'supertrend_bullish', 'supertrend_bearish', 'tenkan_sen', 'kijun_sen', 'senkou_a', 'senkou_b', 'chikou_span', 'psar', 'psar_reversal', 'aroon_up', 'aroon_down', 'aroon_oscillator'],
  greek: ['delta', 'delta_above', 'delta_below', 'delta_near_zero', 'delta_near_half', 'delta_near_one', 'delta_atm', 'delta_itm', 'delta_otm', 'gamma', 'gamma_above', 'gamma_below', 'gamma_high', 'gamma_max', 'theta', 'theta_above', 'theta_below', 'theta_decay', 'theta_accelerating', 'theta_zero_day', 'theta_near_expiry', 'vega', 'vega_above', 'vega_below', 'vega_sensitivity', 'vega_high', 'rho', 'rho_above', 'rho_below'],
  iv: ['iv', 'hv', 'iv_rank', 'iv_percentile', 'iv_hv_ratio', 'iv_above', 'iv_below', 'iv_above_hv', 'iv_below_hv', 'iv_rank_high', 'iv_rank_low', 'iv_expanding', 'iv_contracting', 'iv_change', 'iv_change_pct', 'iv_skew', 'iv_skew_positive', 'iv_skew_negative', 'iv_call_skew', 'iv_put_skew'],
  premium: ['premium', 'bid', 'ask', 'mid_price', 'spread', 'premium_above', 'premium_below', 'intrinsic_value', 'extrinsic_value', 'break_even', 'risk_reward', 'max_profit', 'max_loss'],
  pattern: ['double_top', 'double_bottom', 'head_shoulders', 'wedge', 'triangle', 'flag', 'pennant', 'channel_up', 'channel_down', 'consolidating'],
  time: ['market_open', 'market_close', 'high_volatility', 'low_volatility', 'first_hour', 'last_hour', 'lunch_hour', 'expiry_day', 'week_start', 'week_end', 'month_start', 'month_end'],
  risk: ['max_drawdown', 'position_size', 'account_balance', 'risk_per_trade', 'sharpe_ratio', 'sortino_ratio', 'win_rate', 'profit_factor', 'avg_win', 'avg_loss', 'rr_ratio'],
  order: ['order_pending', 'order_filled', 'order_partial', 'order_cancelled', 'order_rejected', 'order_type', 'order_side', 'order_quantity', 'order_price', 'sl_triggered', 'tp_triggered', 'trailing_sl_triggered', 'bracket_activated', 'trade_open', 'trade_closed', 'entry_price', 'exit_price', 'trade_pnl'],
  string: ['signal', 'signal_buy', 'signal_sell', 'signal_close', 'action', 'symbol', 'symbol_contains', 'exchange', 'option_type', 'is_call', 'is_put'],
  special: ['crosses_above', 'crosses_below', 'value_crosses_above', 'value_crosses_below', 'ema_alignment_up', 'ema_alignment_down', 'at_fib_level', 'near_fib_level', 'near_support', 'near_resistance', 'breakout', 'breakdown'],
}

// Generate unique ID
const generateId = () => Math.random().toString(36).substr(2, 9)

// StrategyBuilder Component
const StrategyBuilder: React.FC = () => {
  // State
  const [strategy, setStrategy] = useState<Strategy>({
    name: 'New Strategy',
    description: '',
    conditionGroups: [
      {
        id: generateId(),
        logic: 'and',
        conditions: [
          { id: generateId(), indicator: null, operator: null, value: '', enabled: true }
        ]
      }
    ],
    tradeLegs: [
      {
        id: generateId(),
        action: 'buy',
        orderType: 'market',
        optionType: 'none',
        quantity: 1,
        stopLoss: { enabled: true, value: 2, type: 'percentage' },
        takeProfit: { enabled: true, value: 5, type: 'percentage' },
        trailingStop: { enabled: false, activationLevel: 3, step: 1 }
      }
    ],
    validationScore: 0,
    errors: []
  })
  
  const [activeTab, setActiveTab] = useState<'conditions' | 'actions' | 'settings'>('conditions')
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set([strategy.conditionGroups[0]?.id]))
  const [draggedItem, setDraggedItem] = useState<{ type: string; data: any } | null>(null)
  const [dropTarget, setDropTarget] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [showValidation, setShowValidation] = useState(false)
  const [activeIndicatorCategory, setActiveIndicatorCategory] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')

  // Filtered indicators based on category and search
  const filteredIndicators = useMemo(() => {
    return INDICATORS.filter(ind => {
      const matchesCategory = activeIndicatorCategory === 'all' || ind.category === activeIndicatorCategory
      const matchesSearch = searchQuery === '' || 
        ind.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ind.shortName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ind.description.toLowerCase().includes(searchQuery.toLowerCase())
      return matchesCategory && matchesSearch
    })
  }, [activeIndicatorCategory, searchQuery])

  // Get keyword description helper
  const getKeywordDescription = (keyword: string): string => {
    const descriptions: Record<string, string> = {
      // Price
      close: 'Current closing price',
      open: 'Opening price',
      high: 'Highest price',
      low: 'Lowest price',
      volume: 'Trading volume',
      price: 'Current price',
      vwap: 'Volume weighted average price',
      // Indicators
      rsi: 'Relative Strength Index (0-100)',
      rsi_overbought: 'RSI above 70',
      rsi_oversold: 'RSI below 30',
      macd: 'MACD line value',
      adx: 'Average Directional Index',
      adx_strong: 'ADX above 25 (strong trend)',
      // Greeks
      delta: 'Option delta (-1 to 1)',
      gamma: 'Option gamma',
      theta: 'Option theta (time decay)',
      vega: 'Option vega (volatility sensitivity)',
      // Special
      crosses_above: 'Indicator crosses above another',
      crosses_below: 'Indicator crosses below another',
      breakout: 'Price breaks resistance',
      breakdown: 'Price breaks support',
    }
    return descriptions[keyword] || keyword
  }

  // Drag handlers
  const handleDragStart = useCallback((e: DragEvent, type: string, data: any) => {
    setDraggedItem({ type, data })
    e.dataTransfer.effectAllowed = 'copy'
    e.dataTransfer.setData('text/plain', JSON.stringify({ type, data }))
  }, [])

  const handleDragOver = useCallback((e: DragEvent, conditionId: string) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
    setDropTarget(conditionId)
  }, [])

  const handleDragLeave = useCallback(() => {
    setDropTarget(null)
  }, [])

  const handleDrop = useCallback((e: DragEvent, conditionId: string) => {
    e.preventDefault()
    setDropTarget(null)
    
    try {
      const data = JSON.parse(e.dataTransfer.getData('text/plain'))
      
      if (data.type === 'indicator') {
        setStrategy(prev => ({
          ...prev,
          conditionGroups: prev.conditionGroups.map(group => ({
            ...group,
            conditions: group.conditions.map(cond => 
              cond.id === conditionId 
                ? { ...cond, indicator: data.data }
                : cond
            )
          }))
        }))
      } else if (data.type === 'operator') {
        setStrategy(prev => ({
          ...prev,
          conditionGroups: prev.conditionGroups.map(group => ({
            ...group,
            conditions: group.conditions.map(cond => 
              cond.id === conditionId 
                ? { ...cond, operator: data.data }
                : cond
            )
          }))
        }))
      }
    } catch (err) {
      console.error('Drop error:', err)
    }
    
    setDraggedItem(null)
  }, [])

  // Condition handlers
  const addCondition = useCallback((groupId: string) => {
    setStrategy(prev => ({
      ...prev,
      conditionGroups: prev.conditionGroups.map(group => 
        group.id === groupId 
          ? { ...group, conditions: [...group.conditions, { id: generateId(), indicator: null, operator: null, value: '', enabled: true }] }
          : group
      )
    }))
  }, [])

  const removeCondition = useCallback((groupId: string, conditionId: string) => {
    setStrategy(prev => ({
      ...prev,
      conditionGroups: prev.conditionGroups.map(group => 
        group.id === groupId 
          ? { ...group, conditions: group.conditions.filter(c => c.id !== conditionId) }
          : group
      )
    }))
  }, [])

  const updateCondition = useCallback((groupId: string, conditionId: string, updates: Partial<Condition>) => {
    setStrategy(prev => ({
      ...prev,
      conditionGroups: prev.conditionGroups.map(group => 
        group.id === groupId 
          ? { ...group, conditions: group.conditions.map(c => c.id === conditionId ? { ...c, ...updates } : c) }
          : group
      )
    }))
  }, [])

  const addConditionGroup = useCallback(() => {
    const newGroupId = generateId()
    setStrategy(prev => ({
      ...prev,
      conditionGroups: [...prev.conditionGroups, {
        id: newGroupId,
        logic: 'and',
        conditions: [{ id: generateId(), indicator: null, operator: null, value: '', enabled: true }]
      }]
    }))
    setExpandedGroups(prev => new Set([...prev, newGroupId]))
  }, [])

  const removeConditionGroup = useCallback((groupId: string) => {
    if (strategy.conditionGroups.length <= 1) return
    setStrategy(prev => ({
      ...prev,
      conditionGroups: prev.conditionGroups.filter(g => g.id !== groupId)
    }))
  }, [strategy.conditionGroups.length])

  const toggleGroupLogic = useCallback((groupId: string) => {
    setStrategy(prev => ({
      ...prev,
      conditionGroups: prev.conditionGroups.map(group => 
        group.id === groupId 
          ? { ...group, logic: group.logic === 'and' ? 'or' : 'and' }
          : group
      )
    }))
  }, [])

  const toggleGroupExpanded = useCallback((groupId: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev)
      if (newSet.has(groupId)) {
        newSet.delete(groupId)
      } else {
        newSet.add(groupId)
      }
      return newSet
    })
  }, [])

  // Trade leg handlers
  const addTradeLeg = useCallback(() => {
    setStrategy(prev => ({
      ...prev,
      tradeLegs: [...prev.tradeLegs, {
        id: generateId(),
        action: 'buy',
        orderType: 'market',
        optionType: 'none',
        quantity: 1,
        stopLoss: { enabled: true, value: 2, type: 'percentage' },
        takeProfit: { enabled: true, value: 5, type: 'percentage' },
        trailingStop: { enabled: false, activationLevel: 3, step: 1 }
      }]
    }))
  }, [])

  const removeTradeLeg = useCallback((legId: string) => {
    if (strategy.tradeLegs.length <= 1) return
    setStrategy(prev => ({
      ...prev,
      tradeLegs: prev.tradeLegs.filter(l => l.id !== legId)
    }))
  }, [strategy.tradeLegs.length])

  const updateTradeLeg = useCallback((legId: string, updates: Partial<TradeLeg>) => {
    setStrategy(prev => ({
      ...prev,
      tradeLegs: prev.tradeLegs.map(leg => leg.id === legId ? { ...leg, ...updates } : leg)
    }))
  }, [])

  // Validation
  const validateStrategy = useCallback(() => {
    const errors: string[] = []
    let score = 0
    const maxScore = 100
    
    // Check name
    if (!strategy.name.trim()) {
      errors.push('Strategy name is required')
    } else {
      score += 10
    }
    
    // Check conditions
    let conditionCount = 0
    let validConditions = 0
    
    strategy.conditionGroups.forEach((group, gIdx) => {
      group.conditions.forEach((cond, cIdx) => {
        conditionCount++
        if (cond.indicator && cond.operator) {
          validConditions++
          score += 15
        } else if (cond.indicator || cond.operator) {
          errors.push(`Group ${gIdx + 1}, Condition ${cIdx + 1}: Missing indicator or operator`)
        } else {
          errors.push(`Group ${gIdx + 1}, Condition ${cIdx + 1}: Empty condition`)
        }
      })
    })
    
    // Check trade legs
    if (strategy.tradeLegs.length === 0) {
      errors.push('At least one trade leg is required')
    } else {
      let validLegs = 0
      strategy.tradeLegs.forEach((leg, idx) => {
        if (leg.action && leg.quantity > 0) {
          validLegs++
          score += 10
        } else {
          errors.push(`Trade leg ${idx + 1}: Invalid configuration`)
        }
      })
    }
    
    // Cap score
    score = Math.min(score, maxScore)
    
    setStrategy(prev => ({ ...prev, validationScore: score, errors }))
    setShowValidation(true)
    
    return { score, errors }
  }, [strategy])

  // Save strategy
  const saveStrategy = useCallback(async () => {
    setIsSaving(true)
    try {
      const validation = validateStrategy()
      if (validation.errors.length > 0 && validation.score < 50) {
        alert('Please fix errors before saving')
        return
      }
      
      // API call would go here
      console.log('Saving strategy:', strategy)
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      alert('Strategy saved successfully!')
    } catch (err) {
      console.error('Save error:', err)
      alert('Failed to save strategy')
    } finally {
      setIsSaving(false)
    }
  }, [strategy, validateStrategy])

  // Reset strategy
  const resetStrategy = useCallback(() => {
    if (confirm('Reset strategy? All changes will be lost.')) {
      setStrategy({
        name: 'New Strategy',
        description: '',
        conditionGroups: [
          {
            id: generateId(),
            logic: 'and',
            conditions: [
              { id: generateId(), indicator: null, operator: null, value: '', enabled: true }
            ]
          }
        ],
        tradeLegs: [
          {
            id: generateId(),
            action: 'buy',
            orderType: 'market',
            optionType: 'none',
            quantity: 1,
            stopLoss: { enabled: true, value: 2, type: 'percentage' },
            takeProfit: { enabled: true, value: 5, type: 'percentage' },
            trailingStop: { enabled: false, activationLevel: 3, step: 1 }
          }
        ],
        validationScore: 0,
        errors: []
      })
      setShowValidation(false)
    }
  }, [])

  // Get indicator color
  const getIndicatorColor = (indicator: Indicator | null) => {
    return indicator?.color || '#64748b'
  }

  return (
    <div className="strategy-builder">
      {/* Header */}
      <div className="sb-header">
        <div className="sb-header-left">
          <h2 className="sb-title">Visual Strategy Builder</h2>
          <input
            type="text"
            className="sb-name-input"
            value={strategy.name}
            onChange={(e) => setStrategy(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Strategy Name"
          />
        </div>
        <div className="sb-header-actions">
          <button className="sb-btn sb-btn-secondary" onClick={resetStrategy}>
            <RotateCcw size={16} /> Reset
          </button>
          <button className="sb-btn sb-btn-secondary" onClick={validateStrategy}>
            <Check size={16} /> Validate
          </button>
          <button className="sb-btn sb-btn-primary" onClick={saveStrategy} disabled={isSaving}>
            <Save size={16} /> {isSaving ? 'Saving...' : 'Save Strategy'}
          </button>
        </div>
      </div>

      {/* Execution Target Selector - TradetrON Integration */}
      <div className="sb-execution-target">
        <div className="execution-label">
          <Zap size={14} />
          <span>Execution Target:</span>
        </div>
        <div className="execution-options">
          <label className={`execution-option ${(strategy.executionTarget === 'ZOBULL' || !strategy.executionTarget) ? 'selected' : ''}`}>
            <input
              type="radio"
              name="executionTarget"
              value="ZOBULL"
              checked={strategy.executionTarget === 'ZOBULL' || !strategy.executionTarget}
              onChange={(e) => setStrategy(prev => ({ ...prev, executionTarget: e.target.value as any }))}
            />
            <span className="option-icon"><Layers size={16} /></span>
            <span className="option-text">ZO Bull</span>
            <span className="option-desc">Execute on ZO Bull</span>
          </label>
          <label className={`execution-option ${strategy.executionTarget === 'TRADETRON' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="executionTarget"
              value="TRADETRON"
              checked={strategy.executionTarget === 'TRADETRON'}
              onChange={(e) => setStrategy(prev => ({ ...prev, executionTarget: e.target.value as any }))}
            />
            <span className="option-icon"><ArrowRight size={16} /></span>
            <span className="option-text">TradetrON</span>
            <span className="option-desc">Execute on TradetrON</span>
          </label>
          <label className={`execution-option ${strategy.executionTarget === 'BOTH' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="executionTarget"
              value="BOTH"
              checked={strategy.executionTarget === 'BOTH'}
              onChange={(e) => setStrategy(prev => ({ ...prev, executionTarget: e.target.value as any }))}
            />
            <span className="option-icon"><Zap size={16} /></span>
            <span className="option-text">Both</span>
            <span className="option-desc">Execute on both</span>
          </label>
        </div>
      </div>

      {/* Validation Score */}
      {showValidation && (
        <div className={`sb-validation ${strategy.errors.length > 0 ? 'has-errors' : 'success'}`}>
          <div className="validation-score">
            <span className="score-label">Completeness Score</span>
            <span className={`score-value ${strategy.validationScore >= 70 ? 'good' : strategy.validationScore >= 40 ? 'medium' : 'low'}`}>
              {strategy.validationScore}%
            </span>
          </div>
          {strategy.errors.length > 0 && (
            <div className="validation-errors">
              {strategy.errors.slice(0, 5).map((err, idx) => (
                <div key={idx} className="validation-error">
                  <AlertCircle size={14} /> {err}
                </div>
              ))}
              {strategy.errors.length > 5 && (
                <div className="validation-error more">+{strategy.errors.length - 5} more errors</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Main Layout */}
      <div className="sb-main">
        {/* Left Panel - Indicator Palette with Search */}
        <div className="sb-palette">
          {/* Search */}
          <div className="palette-search">
            <Search size={14} className="search-icon" />
            <input
              type="text"
              placeholder="Search indicators, keywords..."
              className="palette-search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="search-clear" onClick={() => setSearchQuery('')}>
                ×
              </button>
            )}
          </div>

          {/* Stats */}
          <div className="palette-stats">
            <span className="stat"><Zap size={12} /> {INDICATORS.length} Indicators</span>
            <span className="stat"><Settings size={12} /> {OPERATORS.length} Operators</span>
            <span className="stat"><Code size={12} /> {Object.values(KEYWORD_CATEGORIES).flat().length} Keywords</span>
          </div>

          {/* Indicators Section */}
          <div className="palette-section">
            <h3 className="palette-title">
              <Zap size={16} /> Indicators
              <span className="count">{INDICATORS.length}</span>
            </h3>
            {/* Category Filters */}
            <div className="category-filters">
              {['all', 'momentum', 'trend', 'volatility', 'volume', 'overlay', 'custom'].map(cat => (
                <button key={cat} className={`cat-filter ${activeIndicatorCategory === cat ? 'active' : ''}`} onClick={() => setActiveIndicatorCategory(cat)}>
                  {cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                </button>
              ))}
            </div>
            <div className="palette-items">
              {filteredIndicators.map(indicator => (
                <div
                  key={indicator.id}
                  className="palette-item"
                  draggable
                  onDragStart={(e) => handleDragStart(e, 'indicator', indicator)}
                  style={{ borderLeftColor: indicator.color }}
                  title={indicator.description}
                >
                  <GripVertical size={12} className="drag-handle" />
                  <span className="item-icon" style={{ color: indicator.color }}>{indicator.icon}</span>
                  <div className="item-info">
                    <span className="item-name">{indicator.shortName}</span>
                    <span className="item-desc">{indicator.name}</span>
                  </div>
                  <span className="item-category">{indicator.category}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Operators Section */}
          <div className="palette-section">
            <h3 className="palette-title">
              <Settings size={16} /> Operators
              <span className="count">{OPERATORS.length}</span>
            </h3>
            <div className="palette-items">
              {OPERATORS.map(operator => (
                <div
                  key={operator.id}
                  className="palette-item operator"
                  draggable
                  onDragStart={(e) => handleDragStart(e, 'operator', operator)}
                  title={operator.description}
                >
                  <GripVertical size={12} className="drag-handle" />
                  <span className="item-symbol">{operator.symbol}</span>
                  <div className="item-info">
                    <span className="item-name">{operator.name}</span>
                    <span className="item-desc">{operator.description}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Keywords Section */}
          <div className="palette-section keywords-section">
            <h3 className="palette-title">
              <Code size={16} /> Keyword Functions
              <span className="count">122+</span>
            </h3>
            <div className="keyword-categories">
              {Object.entries(KEYWORD_CATEGORIES).map(([category, keywords]) => (
                <details key={category} className="keyword-category">
                  <summary>
                    <ChevronRight size={14} className="chevron" />
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                    <span className="kw-count">{keywords.length}</span>
                  </summary>
                  <div className="keyword-list">
                    {keywords.map(kw => (
                      <span key={kw} className="keyword-tag" title={getKeywordDescription(kw)}>
                        {kw}
                      </span>
                    ))}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>

        {/* Center Panel - Conditions Canvas */}
        <div className="sb-canvas">
          <div className="canvas-header">
            <h3 className="canvas-title">Conditions</h3>
            <button className="sb-btn sb-btn-sm" onClick={addConditionGroup}>
              <Plus size={14} /> Add Group
            </button>
          </div>

          <div className="condition-groups">
            {strategy.conditionGroups.map((group, gIdx) => (
              <div key={group.id} className="condition-group">
                <div className="group-header" onClick={() => toggleGroupExpanded(group.id)}>
                  <div className="group-header-left">
                    {expandedGroups.has(group.id) ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    <span className="group-label">Group {gIdx + 1}</span>
                    <span className="group-logic" onClick={(e) => { e.stopPropagation(); toggleGroupLogic(group.id); }}>
                      {group.conditions.length} conditions ({group.logic.toUpperCase()})
                    </span>
                  </div>
                  <div className="group-header-right">
                    {strategy.conditionGroups.length > 1 && (
                      <button className="group-action" onClick={(e) => { e.stopPropagation(); removeConditionGroup(group.id); }}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>

                {expandedGroups.has(group.id) && (
                  <div className="group-conditions">
                    {group.conditions.map((condition, cIdx) => (
                      <div
                        key={condition.id}
                        className={`condition-block ${dropTarget === condition.id ? 'drop-target' : ''} ${!condition.enabled ? 'disabled' : ''}`}
                        onDragOver={(e) => handleDragOver(e, condition.id)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, condition.id)}
                      >
                        <div className="condition-grip">
                          <GripVertical size={14} />
                        </div>

                        <div className="condition-content">
                          {/* Indicator Select */}
                          <div className="condition-field indicator-field" style={{ borderColor: getIndicatorColor(condition.indicator) }}>
                            {condition.indicator ? (
                              <div className="selected-indicator" style={{ backgroundColor: `${condition.indicator.color}20` }}>
                                <span style={{ color: condition.indicator.color }}>{condition.indicator.icon}</span>
                                <span className="ind-name">{condition.indicator.shortName}</span>
                                <button className="remove-btn" onClick={() => updateCondition(group.id, condition.id, { indicator: null })}>
                                  <Trash2 size={12} />
                                </button>
                              </div>
                            ) : (
                              <span className="placeholder">Drop Indicator</span>
                            )}
                          </div>

                          {/* Arrow */}
                          <ArrowRight size={16} className="condition-arrow" />

                          {/* Operator Select */}
                          <div className="condition-field operator-field">
                            {condition.operator ? (
                              <div className="selected-operator">
                                <span className="op-symbol">{condition.operator.symbol}</span>
                                <span className="op-name">{condition.operator.name}</span>
                                <button className="remove-btn" onClick={() => updateCondition(group.id, condition.id, { operator: null })}>
                                  <Trash2 size={12} />
                                </button>
                              </div>
                            ) : (
                              <span className="placeholder">Drop Operator</span>
                            )}
                          </div>

                          {/* Value Input */}
                          {condition.operator?.requiresValue && (
                            <>
                              <ArrowRight size={16} className="condition-arrow" />
                              <input
                                type="text"
                                className="condition-value"
                                value={condition.value}
                                onChange={(e) => updateCondition(group.id, condition.id, { value: e.target.value })}
                                placeholder="Value"
                              />
                            </>
                          )}
                        </div>

                        <div className="condition-actions">
                          <label className="enable-toggle">
                            <input
                              type="checkbox"
                              checked={condition.enabled}
                              onChange={(e) => updateCondition(group.id, condition.id, { enabled: e.target.checked })}
                            />
                            <span className="toggle-slider"></span>
                          </label>
                          <button className="condition-action" onClick={() => removeCondition(group.id, condition.id)}>
                            <Copy size={14} />
                          </button>
                          {group.conditions.length > 1 && (
                            <button className="condition-action danger" onClick={() => removeCondition(group.id, condition.id)}>
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}

                    <button className="add-condition-btn" onClick={() => addCondition(group.id)}>
                      <Plus size={14} /> Add Condition
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Right Panel - Trade Legs */}
        <div className="sb-actions">
          <div className="actions-header">
            <h3 className="actions-title">Trade Actions</h3>
            <button className="sb-btn sb-btn-sm" onClick={addTradeLeg}>
              <Plus size={14} /> Add Leg
            </button>
          </div>

          <div className="trade-legs">
            {strategy.tradeLegs.map((leg, idx) => (
              <div key={leg.id} className="trade-leg">
                <div className="leg-header">
                  <span className="leg-label">Trade Leg {idx + 1}</span>
                  {strategy.tradeLegs.length > 1 && (
                    <button className="leg-action" onClick={() => removeTradeLeg(leg.id)}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>

                <div className="leg-fields">
                  {/* Action */}
                  <div className="leg-field">
                    <label>Action</label>
                    <div className="action-buttons">
                      <button
                        className={`action-btn ${leg.action === 'buy' ? 'active buy' : ''}`}
                        onClick={() => updateTradeLeg(leg.id, { action: 'buy' })}
                      >
                        Buy
                      </button>
                      <button
                        className={`action-btn ${leg.action === 'sell' ? 'active sell' : ''}`}
                        onClick={() => updateTradeLeg(leg.id, { action: 'sell' })}
                      >
                        Sell
                      </button>
                      <button
                        className={`action-btn ${leg.action === 'close' ? 'active close' : ''}`}
                        onClick={() => updateTradeLeg(leg.id, { action: 'close' })}
                      >
                        Close
                      </button>
                    </div>
                  </div>

                  {/* Order Type */}
                  <div className="leg-field">
                    <label>Order Type</label>
                    <select value={leg.orderType} onChange={(e) => updateTradeLeg(leg.id, { orderType: e.target.value as any })}>
                      <option value="market">Market</option>
                      <option value="limit">Limit</option>
                      <option value="stop">Stop</option>
                    </select>
                  </div>

                  {/* Quantity */}
                  <div className="leg-field">
                    <label>Quantity</label>
                    <input
                      type="number"
                      value={leg.quantity}
                      onChange={(e) => updateTradeLeg(leg.id, { quantity: parseInt(e.target.value) || 1 })}
                      min="1"
                    />
                  </div>

                  {/* Stop Loss */}
                  <div className="leg-field sl-field">
                    <label>
                      <input
                        type="checkbox"
                        checked={leg.stopLoss.enabled}
                        onChange={(e) => updateTradeLeg(leg.id, { 
                          stopLoss: { ...leg.stopLoss, enabled: e.target.checked }
                        })}
                      />
                      Stop Loss
                    </label>
                    {leg.stopLoss.enabled && (
                      <div className="sl-inputs">
                        <input
                          type="number"
                          value={leg.stopLoss.value}
                          onChange={(e) => updateTradeLeg(leg.id, { 
                            stopLoss: { ...leg.stopLoss, value: parseFloat(e.target.value) || 0 }
                          })}
                        />
                        <select
                          value={leg.stopLoss.type}
                          onChange={(e) => updateTradeLeg(leg.id, { 
                            stopLoss: { ...leg.stopLoss, type: e.target.value as any }
                          })}
                        >
                          <option value="percentage">%</option>
                          <option value="points">Pts</option>
                        </select>
                      </div>
                    )}
                  </div>

                  {/* Take Profit */}
                  <div className="leg-field tp-field">
                    <label>
                      <input
                        type="checkbox"
                        checked={leg.takeProfit.enabled}
                        onChange={(e) => updateTradeLeg(leg.id, { 
                          takeProfit: { ...leg.takeProfit, enabled: e.target.checked }
                        })}
                      />
                      Take Profit
                    </label>
                    {leg.takeProfit.enabled && (
                      <div className="tp-inputs">
                        <input
                          type="number"
                          value={leg.takeProfit.value}
                          onChange={(e) => updateTradeLeg(leg.id, { 
                            takeProfit: { ...leg.takeProfit, value: parseFloat(e.target.value) || 0 }
                          })}
                        />
                        <select
                          value={leg.takeProfit.type}
                          onChange={(e) => updateTradeLeg(leg.id, { 
                            takeProfit: { ...leg.takeProfit, type: e.target.value as any }
                          })}
                        >
                          <option value="percentage">%</option>
                          <option value="points">Pts</option>
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default StrategyBuilder
